'use strict';

/**
 * T372 — Sealed-box per-record envelope (server-blind-at-rest chat history).
 *
 * A sealed-box (age / libsodium `crypto_box_seal`) construction with a
 * PQ-hybrid KEM. The SERVER seals each record with the user's PUBLIC kex key
 * (non-secret, server-readable) and is mathematically unable to decapsulate
 * (read) what it sealed; the CLIENT decrypts only on read with its
 * vault-resident PRIVATE kex key (browser-only, never on the server).
 *
 * NEW glue (LD-10) — composes existing primitives, NOT a new crypto primitive:
 *   - hybrid-kex `encapsulate` / `decapsulate` (X25519 + ML-KEM-768, HKDF-combined
 *     `sharedSecret` used DIRECTLY as the AES-256 key — NO separate DEK/HKDF, LD-2)
 *   - `@noble/ciphers` `gcmsiv` used DIRECTLY (pure-JS → runs in browser AND
 *     server; nonce-misuse-resistant + key-committing; NOT the node:crypto
 *     `siv-symmetric.js` wrapper, whose require is unaliased in the build, LD-3)
 *
 * Envelope (self-describing): { v, seal:'cbox-v1', alg, kexVersion,
 * kem:{kemCiphertext, x25519Eph}, nonce, seq, prevHash, ct }. `kexVersion`
 * (T513) is the hybrid-kex combiner version the record was sealed under —
 * ABSENT on every pre-T513 record, which means the retired v1 combiner; new
 * seals stamp KEX_CURRENT. The `seq` + `prevHash` fields are
 * WRITTEN + AAD-bound from P1 (format-forward, LD-MVP-INTEGRITY-SPLIT) so the
 * integrity follow-on enables chain VERIFICATION with no data re-migration.
 *
 * AAD canonicalization (LD-TAMPER-CHAIN): the AAD byte layout is PINNED so
 * server-seal and browser-unseal cannot diverge —
 *     marker ‖ kem(kemCiphertext ‖ x25519Eph) ‖ seq(BE u64) ‖ prevHash
 * An AAD mismatch is an AEAD auth failure, never a silent pass.
 */

const { encapsulate, decapsulate, KEX_V1 } = require('./hybrid-kex');
const { gcmsiv } = require('@noble/ciphers/aes.js');

// SHA-256 — Node has it via node:crypto; the browser bundle (esbuild) shims
// node:crypto's `createHash`, so this resolves on both sides.
const crypto = require('crypto');

const SEAL_MARKER = 'cbox-v1';
const ALG = 'x25519+mlkem768/aes256gcmsiv';
const ENVELOPE_VERSION = 1;

// Per-day-file chain genesis (LD-TAMPER-CHAIN — genesis is PER-FILE, not
// per-account): the first record in a chain links this fixed constant.
const GENESIS_PREV_HASH = '0'.repeat(64);

// Size buckets (LD-18 metadata minimization): pad the *plaintext* to the next
// bucket before sealing so the server learns only a bucketed record size, not
// the exact length. Buckets in bytes.
const SIZE_BUCKETS = [256, 1024, 4096, 16384, 65536, 262144, 1048576];

const NONCE_LEN = 12; // gcmsiv nonce

function toU8(x) {
  if (x instanceof Uint8Array) return x;
  if (Array.isArray(x)) return Uint8Array.from(x);
  throw new Error('expected Uint8Array');
}
function hex(u8) {
  return Buffer.from(toU8(u8)).toString('hex');
}
function unhex(h) {
  if (typeof h !== 'string') throw new Error('expected hex string');
  return new Uint8Array(Buffer.from(h, 'hex'));
}

/**
 * Pick the smallest size bucket that fits `len` bytes. Payloads larger than
 * the biggest bucket pad to the next multiple of the biggest bucket (so very
 * large records still bucket, just coarsely).
 */
function bucketFor(len) {
  for (const b of SIZE_BUCKETS) {
    if (len <= b) return b;
  }
  const biggest = SIZE_BUCKETS[SIZE_BUCKETS.length - 1];
  return Math.ceil(len / biggest) * biggest;
}

/**
 * Length-prefixed pad: [u32 BE actual-length][actual bytes][zero pad up to bucket].
 * Reversible — unpad reads the prefix and slices the exact bytes back.
 */
function pad(plainBytes) {
  const total = bucketFor(plainBytes.length + 4);
  const out = new Uint8Array(total);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, plainBytes.length, false); // big-endian length prefix
  out.set(plainBytes, 4);
  return out;
}
function unpad(paddedBytes) {
  const dv = new DataView(paddedBytes.buffer, paddedBytes.byteOffset, paddedBytes.byteLength);
  const n = dv.getUint32(0, false);
  if (n + 4 > paddedBytes.length) throw new Error('seal-box: corrupt pad length');
  return paddedBytes.subarray(4, 4 + n);
}

/**
 * Canonical AAD (LD-TAMPER-CHAIN). PINNED byte layout, identical on both
 * sides: marker ‖ kemCiphertext ‖ x25519Eph ‖ seq(BE u64) ‖ prevHash(raw bytes).
 *
 * @param {string} marker          the seal marker string ('cbox-v1')
 * @param {{kemCiphertext:Uint8Array, x25519Eph:Uint8Array}} kem
 * @param {number} seq             monotonic per-file sequence (>= 0)
 * @param {string} prevHash        hex (64 chars) of the prior record / genesis
 * @returns {Uint8Array}
 */
function canonicalAad(marker, kem, seq, prevHash) {
  const markerBytes = new TextEncoder().encode(marker);
  const kemCt = toU8(kem.kemCiphertext);
  const eph = toU8(kem.x25519Eph);
  const seqBuf = new Uint8Array(8);
  // BE u64; JS numbers are safe for seq counts well within 2^53.
  new DataView(seqBuf.buffer).setBigUint64(0, BigInt(seq >>> 0 === seq ? seq : Math.trunc(seq)), false);
  const prevBytes = unhex(prevHash);
  const out = new Uint8Array(markerBytes.length + kemCt.length + eph.length + seqBuf.length + prevBytes.length);
  let o = 0;
  out.set(markerBytes, o); o += markerBytes.length;
  out.set(kemCt, o); o += kemCt.length;
  out.set(eph, o); o += eph.length;
  out.set(seqBuf, o); o += seqBuf.length;
  out.set(prevBytes, o);
  return out;
}

/**
 * Hash a sealed envelope for the backward chain (next record's prevHash).
 * Hashes the IMMUTABLE sealed fields (marker, kem, nonce, seq, prevHash, ct)
 * so any tamper changes the link. Returns hex.
 */
function recordHash(envelope) {
  const h = crypto.createHash('sha256');
  h.update(String(envelope.seal || ''));
  h.update(String(envelope.kem && envelope.kem.kemCiphertext || ''));
  h.update(String(envelope.kem && envelope.kem.x25519Eph || ''));
  h.update(String(envelope.nonce || ''));
  h.update(String(envelope.seq));
  h.update(String(envelope.prevHash || ''));
  h.update(String(envelope.ct || ''));
  return h.digest('hex');
}

/**
 * Normalize a recipient public kex key to { x25519, mlKem } Uint8Arrays.
 * Accepts either Uint8Arrays (in-process / browser) or hex strings (server
 * `meta.publicKeys` serialized-for-storage shape).
 */
function normalizePubKex(pub) {
  if (!pub || typeof pub !== 'object') throw new Error('seal-box: missing recipient public kex key');
  const x = pub.x25519, m = pub.mlKem;
  const toBytes = (v) => {
    if (v instanceof Uint8Array) return v;
    if (v && typeof v === 'object' && typeof v.__u8 === 'string') return unhex(v.__u8); // serializeForStorage shape
    if (typeof v === 'string') return unhex(v);
    if (Array.isArray(v)) return Uint8Array.from(v);
    throw new Error('seal-box: unrecognized public key field encoding');
  };
  return { x25519: toBytes(x), mlKem: toBytes(m) };
}

function normalizePrivKex(priv) {
  if (!priv || typeof priv !== 'object') throw new Error('seal-box: missing private kex key');
  const toBytes = (v) => {
    if (v instanceof Uint8Array) return v;
    if (v && typeof v === 'object' && typeof v.__u8 === 'string') return unhex(v.__u8);
    if (typeof v === 'string') return unhex(v);
    if (Array.isArray(v)) return Uint8Array.from(v);
    throw new Error('seal-box: unrecognized private key field encoding');
  };
  return { x25519: toBytes(priv.x25519), mlKem: toBytes(priv.mlKem) };
}

/**
 * Seal a single record (SERVER side — public key only).
 *
 * @param {object} entry             the plaintext record (JSON-serializable)
 * @param {{x25519,mlKem}} recipientPubKex  user's PUBLIC kex key
 * @param {{seq:number, prevHash:string}} chain  format-forward chain fields
 * @returns {object} the cbox-v1 envelope
 */
function sealRecord(entry, recipientPubKex, chain) {
  const pub = normalizePubKex(recipientPubKex);
  const seq = (chain && Number.isInteger(chain.seq)) ? chain.seq : 0;
  const prevHash = (chain && typeof chain.prevHash === 'string') ? chain.prevHash : GENESIS_PREV_HASH;

  // Fresh ephemeral per record (LD-2): encapsulate → { kemCiphertext,
  // x25519EphemeralPublic, sharedSecret }. sharedSecret is the AES-256 key
  // DIRECTLY (already HKDF-combined; NO extra DEK/HKDF).
  const { kemCiphertext, x25519EphemeralPublic, sharedSecret, kexVersion } = encapsulate(pub);
  const kem = { kemCiphertext, x25519Eph: x25519EphemeralPublic };

  const nonce = crypto.randomBytes(NONCE_LEN);
  const aad = canonicalAad(SEAL_MARKER, kem, seq, prevHash);
  const plain = new TextEncoder().encode(JSON.stringify(entry));
  const padded = pad(plain);
  const ct = gcmsiv(toU8(sharedSecret), toU8(nonce), aad).encrypt(padded);

  return {
    v: ENVELOPE_VERSION,
    seal: SEAL_MARKER,
    alg: ALG,
    kexVersion, // T513 — combiner version (KEX_CURRENT); absent on pre-T513 records
    kem: { kemCiphertext: hex(kemCiphertext), x25519Eph: hex(x25519EphemeralPublic) },
    nonce: hex(nonce),
    seq,
    prevHash,
    ct: hex(ct),
  };
}

/**
 * Unseal a single record (CLIENT side — requires the PRIVATE kex key).
 * Fails CLOSED on tamper / wrong key / AAD mismatch (throws).
 *
 * @param {object} envelope         a cbox-v1 envelope
 * @param {{x25519,mlKem}} privKex  user's PRIVATE kex key (vault-resident)
 * @returns {object} the recovered plaintext record
 */
function unsealRecord(envelope, privKex) {
  if (!envelope || envelope.seal !== SEAL_MARKER) {
    throw new Error('seal-box: not a cbox-v1 envelope');
  }
  const priv = normalizePrivKex(privKex); // throws if a public key (no x25519/mlKem private bytes of right size)
  const kemCt = unhex(envelope.kem.kemCiphertext);
  const eph = unhex(envelope.kem.x25519Eph);
  // decapsulate validates the private key shape (32 / 2400 byte halves) and
  // throws for a public key — this is what makes the server (public key only)
  // provably unable to open what it sealed.
  // T513 — a pre-T513 record carries NO kexVersion: it was sealed under the
  // retired v1 combiner, so ABSENT means v1 (the only version that existed).
  // A present-but-unknown value is refused by decapsulate (frozen allowlist);
  // a wrong version derives a different key and fails closed at gcmsiv below.
  const kexVersion = envelope.kexVersion === undefined ? KEX_V1 : envelope.kexVersion;
  const sharedSecret = decapsulate(priv, kemCt, eph, kexVersion);

  const kem = { kemCiphertext: kemCt, x25519Eph: eph };
  const aad = canonicalAad(envelope.seal, kem, envelope.seq, envelope.prevHash);
  const nonce = unhex(envelope.nonce);
  const ct = unhex(envelope.ct);
  const padded = gcmsiv(toU8(sharedSecret), toU8(nonce), aad).decrypt(ct); // throws on auth fail
  const plain = unpad(padded);
  return JSON.parse(new TextDecoder().decode(plain));
}

module.exports = {
  SEAL_MARKER,
  ALG,
  ENVELOPE_VERSION,
  GENESIS_PREV_HASH,
  SIZE_BUCKETS,
  canonicalAad,
  recordHash,
  sealRecord,
  unsealRecord,
  // exported for the integrity follow-on + tests
  _internals: { pad, unpad, bucketFor, normalizePubKex, normalizePrivKex },
};
